/**
 * Everything the reports read, and the writes that sit beside them.
 *
 * The types are the API's own, from the generated schema, so a figure renamed or removed on
 * the server is a compile error here rather than an empty column in a report.
 *
 * It has two consumers, on purpose. The reports area is one. The other is the duty page
 * under settings, which imports `daysOfWeek`, `dutyLimits`, `DutySettings`, `DutyRate` and
 * the four `duty`/`rates` calls: what a day worked is and what it costs is one set of
 * shapes, and the shift book computes every one of its figures from them. Two copies of
 * `DutySettings` that drifted by a field would be a report and a settings screen quietly
 * disagreeing about the firm's own week. Narrowing or moving this file means looking at
 * `web/console/src/settings/duty/` as well.
 *
 * The writes — a duty rate, the duty settings, a driver payment — deliberately do not go to
 * the reporting endpoints. Reporting reads and never writes; the buttons post to Billing and
 * to the tenant settings, and the screen reads again afterwards. The rule governs which
 * module holds the pen, not which screen carries a button. Joining a row of Адреси за
 * заключване to a place is the same: it is `placesApi.join`, in `../places/api.ts`, and the
 * host writes it into Orders, Customers and Geo's ledger. Taking a row off that report is too:
 * dismissing its bookings is `ordersApi.dismissAddresses` and deleting its imported addresses
 * `customersApi.removeImportedAddresses`, both in `../orders/api.ts`.
 */

import { download, request, type Narrow, type Schema } from '@taxi/api-client';

import { periodQuery, type Period } from './period';

// --- The period control ------------------------------------------------------

export { defaultPeriod, periodPresets, periodQuery, type Period, type PeriodPreset } from './period';

// --- What a day worked is, and what it costs ---------------------------------

export const daysOfWeek = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;

export type DayOfWeek = (typeof daysOfWeek)[number];

export type DutySettings = Narrow<Schema<'DutySettings'>, { weekStartsOn: DayOfWeek }>;

/** The bounds the API enforces, repeated only so a number input can carry them. */
export const dutyLimits = {
  dutyMinimumMinutes: { min: 0, max: 1440 },
  silentShiftTimeoutMinutes: { min: 5, max: 720 },
  maxShiftHours: { min: 1, max: 48 },
} as const;

export type DutyRate = Schema<'DutyRateView'>;

/** A new version. Never an edit of an existing one — see the duty page under settings. */
export type DutyRateDraft = Schema<'DutyRateDraft'>;

export type DriverPayment = Schema<'DriverPaymentView'>;

// --- The shift book ----------------------------------------------------------

export type Takings = Schema<'Takings'>;

/**
 * How a session stopped. An ordinary `SignedOff` is the absence of a mark; every other
 * ending is called out beside the session, because a shift somebody did not close himself
 * is the first thing a driver disputes.
 */
export type SessionEnding =
  | 'SignedOff'
  | 'SweptOff'
  | 'Capped'
  | 'Running'
  | 'EndedByOffice';

export type ShiftBookSession = Schema<'ShiftBookSession'>;

// Takings are null only on a driver's own dues, where his firm keeps them off his phone
// (#410). The office's shift book always carries them.
export type ShiftBookDay = Narrow<Schema<'ShiftBookDay'>, { takings: Schema<'Takings'> }>;

export type ShiftBookWeek = Schema<'ShiftBookWeek'>;

export type DriverShiftBook = Narrow<
  Schema<'DriverShiftBook'>,
  { takings: Schema<'Takings'>; days: ShiftBookDay[] }
>;

export type FleetShiftBookRow = Schema<'FleetShiftBookRow'>;

export type FleetShiftBook = Schema<'FleetShiftBook'>;

export type DutyGridCell = Schema<'DutyGridCell'>;

export type DutyGridRow = Schema<'DutyGridRow'>;

export type DutyGrid = Schema<'DutyGrid'>;

// --- When and where ----------------------------------------------------------

export type DemandCell = Schema<'DemandCell'>;

export type DemandZone = Schema<'DemandZone'>;

export type CancellationCount = Schema<'CancellationCount'>;

export type DemandReport = Schema<'DemandReportResult'>;

// --- Service -----------------------------------------------------------------

export type WaitByHour = Schema<'WaitByHour'>;

export type DriverServiceRow = Schema<'DriverServiceRow'>;

export type RatingSummary = Schema<'RatingSummary'>;

export type ServiceReport = Schema<'ServiceReportResult'>;

// --- Clients -----------------------------------------------------------------

export type ClientRow = Schema<'ClientRow'>;

export type LostClientRow = Schema<'LostClientRow'>;

export type LoyaltyBand = Schema<'LoyaltyBand'>;

export type ClientReport = Schema<'ClientReportResult'>;

// --- Calls -------------------------------------------------------------------

export type CallArchive = Schema<'CallArchive'>;

// --- Journeys ----------------------------------------------------------------

export type JourneyList = Schema<'JourneyList'>;

export type JourneyDetail = Schema<'JourneyDetail'>;

/** A page of the pickups booked most that are not yet the firm's own addresses (#434). */
export type AddressesToLockPage = Schema<'AddressesToLockPage'>;
export type AddressToLock = Schema<'AddressToLock'>;

/** The locked place a row most plainly is, suggested on it before anybody asks. */
export type PlaceHint = Schema<'PlaceHint'>;

/** The tiles above Адреси за заключване: how many rows wait each way in. */
export type AddressesToLockSummary = Schema<'AddressesToLockSummary'>;

/** What one of those rows is made of: every booking or imported address, with who booked it. */
export type UnplacedMembers = Schema<'UnplacedMembers'>;
export type UnplacedMember = Schema<'UnplacedMember'>;
export type DirectoryPage = Schema<'DirectoryPage'>;
export type DirectoryRow = Schema<'DirectoryRow'>;
export type CustomerCardView = Schema<'CustomerCardView'>;
export type CardAddress = Schema<'CardAddress'>;

/** Which customers Картотека shows. */
export type DirectoryOrigin = 'All' | 'Imported' | 'Here';

/** How Картотека orders them. */
export type DirectorySort = 'Recent' | 'MostUsed';

// --- The client --------------------------------------------------------------

export const reportsApi = {
  duty: () => request<DutySettings>('/api/tenant/settings/duty'),

  saveDuty: (settings: DutySettings) =>
    request<DutySettings>('/api/tenant/settings/duty', { method: 'PUT', body: settings }),

  rates: () => request<DutyRate[]>('/api/tenant/duty-rates'),

  introduceRate: (rate: DutyRateDraft) =>
    request<DutyRate>('/api/tenant/duty-rates', { method: 'POST', body: rate }),

  endRate: (rateId: string, effectiveTo: string | null) =>
    request<DutyRate>(`/api/tenant/duty-rates/${rateId}/end`, {
      method: 'POST',
      body: { effectiveTo },
    }),

  payments: (driverId: string) =>
    request<DriverPayment[]>(`/api/tenant/driver-payments/${driverId}`),

  recordPayment: (payment: {
    driverId: string;
    amount: number;
    currency?: string | null;
    note?: string | null;
    periodFrom?: string | null;
    periodTo?: string | null;
  }) => request<DriverPayment>('/api/tenant/driver-payments', { method: 'POST', body: payment }),

  reversePayment: (paymentId: string, reason: string) =>
    request<DriverPayment>(`/api/tenant/driver-payments/${paymentId}/reverse`, {
      method: 'POST',
      body: { reason },
    }),

  fleet: (period: Period) =>
    request<FleetShiftBook>(`/api/tenant/reports/shift-book?${periodQuery(period)}`),

  driver: (driverId: string, period: Period) =>
    request<DriverShiftBook>(
      `/api/tenant/reports/shift-book/${driverId}?${periodQuery(period)}`,
    ),

  grid: (period: Period) =>
    request<DutyGrid>(`/api/tenant/reports/shift-book/grid?${periodQuery(period)}`),

  demand: (period: Period) =>
    request<DemandReport>(`/api/tenant/reports/demand?${periodQuery(period)}`),

  service: (period: Period) =>
    request<ServiceReport>(`/api/tenant/reports/service?${periodQuery(period)}`),

  clients: (period: Period) =>
    request<ClientReport>(`/api/tenant/reports/clients?${periodQuery(period)}`),

  /**
   * A page of Адреси за заключване.
   *
   * @param query From `addressesQuery` in `addressesQuery.ts`: the filter — `Google` for the
   *   addresses Google named, the ones paid for; `Map` for the rest (#478); `Imported` for what
   *   the firm's previous system had placed, whatever the period — the order, the page and the
   *   search, as `&name=value` pairs, or empty.
   */
  unplaced: (period: Period, query: string) =>
    request<AddressesToLockPage>(`/api/tenant/reports/unplaced-pickups?${periodQuery(period)}${query}`),

  /**
   * One row's members, by the row's key — found by any member, so a newer booking does not lose
   * it — in the filter and the view of Внесени the row was listed under.
   */
  unplacedMembers: (
    period: Period,
    key: string,
    source: 'all' | 'Google' | 'Map' | 'Imported',
    view: 'Waiting' | 'NoPoint' = 'Waiting',
  ) =>
    request<UnplacedMembers>(
      `/api/tenant/reports/unplaced-pickups/members?${periodQuery(period)}&key=${encodeURIComponent(key)}${
        source === 'all' ? '' : `&source=${source}`
      }${source === 'Imported' && view === 'NoPoint' ? '&view=NoPoint' : ''}`,
    ),

  /** The tiles above Адреси за заключване: booked over the period, imported whatever it is. */
  unplacedSummary: (period: Period) =>
    request<AddressesToLockSummary>(`/api/tenant/reports/unplaced-pickups/summary?${periodQuery(period)}`),

  /** Картотека: a page of every customer the firm knows, the imported ones included. */
  directory: (query: string, origin: DirectoryOrigin, sort: DirectorySort, page: number) =>
    request<DirectoryPage>(
      `/api/tenant/reports/directory?${new URLSearchParams({
        q: query,
        origin,
        sort,
        page: String(page),
      }).toString()}`,
    ),

  /** One customer's card: who, every address, which are locked, and their orders. */
  customerCard: (customerId: string) =>
    request<CustomerCardView>(`/api/tenant/reports/directory/${encodeURIComponent(customerId)}`),

  /** @param filters From `filterQuery` in `callArchive.ts`: `&q=…&outcome=…`, or empty. */
  calls: (period: Period, filters: string) =>
    request<CallArchive>(`/api/tenant/reports/calls?${periodQuery(period)}${filters}`),

  /** @param filters From `filterQuery` in `journey.ts`: `&q=…&problems=…`, or empty. */
  journeys: (period: Period, filters: string) =>
    request<JourneyList>(`/api/tenant/reports/journeys?${periodQuery(period)}${filters}`),

  journey: (orderId: string) => request<JourneyDetail>(`/api/tenant/reports/journeys/${orderId}`),
};

/**
 * Downloads one journey as GPX — the file any map program opens — through the authenticated
 * client, for the same reason as {@link exportReport}.
 */
export async function downloadJourney(orderId: string): Promise<void> {
  const { blob, filename } = await download(`/api/tenant/reports/journeys/${orderId}/gpx`);

  hand(blob, filename ?? 'journey.gpx');
}

/**
 * Downloads a report over the period on screen, and hands it to the browser.
 *
 * Through the authenticated client, because a plain link carries no bearer token and
 * quietly returns a 401 — which arrives as a spreadsheet full of JSON in front of whoever
 * asked for the file.
 *
 * @param extra Anything else the report on screen is narrowed by, as `&name=value` pairs —
 *   the call archive's filters — so the file holds what the screen holds.
 */
export async function exportReport(path: string, period: Period, extra = ''): Promise<void> {
  const { blob, filename } = await download(`${path}?${periodQuery(period, 'csv')}${extra}`);

  hand(blob, filename ?? 'report.csv');
}

/** Gives a downloaded file to the browser to save. */
function hand(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;

  document.body.append(link);
  link.click();
  link.remove();

  // Revoked on the next turn rather than immediately: Safari has been known to cancel a
  // download whose object URL was released in the same tick as the click.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
